import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Cookies from 'js-cookie';

// Types
export type ConsentStatus = 'pending' | 'accepted' | 'rejected' | 'dismissed' | 'customized';

export interface CookiePreferences {
  necessary: boolean; // Always true, can't be changed
  language: boolean;
  theme: boolean;
}

interface CookieConsentContextType {
  consentStatus: ConsentStatus;
  cookiePreferences: CookiePreferences;
  acceptAllCookies: () => void;
  rejectCookies: () => void;
  dismissConsent: () => void;
  customizeCookies: (preferences: CookiePreferences) => void;
  showConsentBanner: boolean;
}

// Create context
const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

// Hook to use the context
export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

// Default cookie preferences
const defaultPreferences: CookiePreferences = {
  necessary: true,
  language: false,
  theme: false
};

// Central GDPR art. 7(1) evidence store — best-effort, the cookie record
// stays the source of truth for the banner itself.
const CONSENT_LOG_URL = 'https://wtjqwipbjbjicdnkpdiq.supabase.co/functions/v1/consent-log';
const CONSENT_SITE_KEY = '3a30382b367464f0d5408a4cf98d026a';
const BANNER_VERSION = 'v1';
const ANON_KEY = 'consentAnonId';

function getAnonId(): string | null {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id && window.crypto && crypto.randomUUID) {
      id = crypto.randomUUID();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function logConsent(action: 'accept_all' | 'reject_all' | 'custom' | 'withdraw', prefs: CookiePreferences): void {
  try {
    const anonId = getAnonId();
    if (!anonId || typeof fetch !== 'function') return;
    fetch(CONSENT_LOG_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        siteKey: CONSENT_SITE_KEY,
        anonId,
        action,
        categories: {
          essential: true,
          analytics: false,
          marketing: false,
          preferences: prefs.language || prefs.theme
        },
        bannerVersion: BANNER_VERSION,
        lang: document.documentElement.lang || 'pt'
      }),
      keepalive: true
    }).catch(() => { /* best-effort */ });
  } catch { /* ignore */ }
}

// Provider component
interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(() => {
    // Get the consent status from cookies
    const savedConsent = Cookies.get('cookieConsent');
    return (savedConsent as ConsentStatus) || 'pending';
  });
  
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>(() => {
    // Get saved preferences or use defaults
    const savedPreferences = Cookies.get('cookiePreferences');
    if (savedPreferences) {
      try {
        return JSON.parse(savedPreferences);
      } catch (e) {
        return { ...defaultPreferences };
      }
    }
    return { ...defaultPreferences };
  });
  
  const [showConsentBanner, setShowConsentBanner] = useState<boolean>(false);
  
  // Show banner only if consent is pending
  useEffect(() => {
    if (consentStatus === 'pending') {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setShowConsentBanner(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    } else {
      setShowConsentBanner(false);
    }
  }, [consentStatus]);
  
  // Apply cookie preferences
  useEffect(() => {
    if (consentStatus === 'accepted' || consentStatus === 'customized') {
      // Save preferences
      Cookies.set('cookiePreferences', JSON.stringify(cookiePreferences), { expires: 365 });
      
      // Remove cookies that are not accepted
      if (!cookiePreferences.language) {
        Cookies.remove('language');
      }
      
      if (!cookiePreferences.theme) {
        Cookies.remove('theme');
      }
    }
  }, [consentStatus, cookiePreferences]);
  
  const acceptAllCookies = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      language: true,
      theme: true
    };
    
    setCookiePreferences(allAccepted);
    setConsentStatus('accepted');
    setShowConsentBanner(false);
    
    // Set a cookie that expires in 1 year
    Cookies.set('cookieConsent', 'accepted', { expires: 365 });
    Cookies.set('cookiePreferences', JSON.stringify(allAccepted), { expires: 365 });
    logConsent('accept_all', allAccepted);
  };
  
  const rejectCookies = () => {
    const hadGrants = consentStatus === 'accepted' || consentStatus === 'customized';
    setCookiePreferences({ ...defaultPreferences });
    setConsentStatus('rejected');
    setShowConsentBanner(false);
    logConsent(hadGrants ? 'withdraw' : 'reject_all', { ...defaultPreferences });

    // Set a session cookie (expires when browser closes)
    Cookies.set('cookieConsent', 'rejected');
    
    // Remove any existing cookies
    Cookies.remove('language');
    Cookies.remove('theme');
    Cookies.remove('cookiePreferences');
  };
  
  const dismissConsent = () => {
    setConsentStatus('dismissed');
    setShowConsentBanner(false);
    
    // Set a cookie that expires in 1 day
    Cookies.set('cookieConsent', 'dismissed', { expires: 1 });
  };
  
  const customizeCookies = (preferences: CookiePreferences) => {
    // Ensure necessary cookies are always accepted
    const updatedPreferences = {
      ...preferences,
      necessary: true
    };
    
    setCookiePreferences(updatedPreferences);
    setConsentStatus('customized');
    setShowConsentBanner(false);
    
    // Set cookies
    Cookies.set('cookieConsent', 'customized', { expires: 365 });
    Cookies.set('cookiePreferences', JSON.stringify(updatedPreferences), { expires: 365 });
    logConsent('custom', updatedPreferences);
    
    // Remove cookies that are not accepted
    if (!updatedPreferences.language) {
      Cookies.remove('language');
    }
    
    if (!updatedPreferences.theme) {
      Cookies.remove('theme');
    }
  };
  
  const value = {
    consentStatus,
    cookiePreferences,
    acceptAllCookies,
    rejectCookies,
    dismissConsent,
    customizeCookies,
    showConsentBanner
  };
  
  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export default CookieConsentContext; 